import type { QueryValue } from '../ofApi'

/**
 * The Queue's calendar math, as OnlyFans' /my/queue does it (live calls
 * 2026-10-08, SPEC §2): Sunday-first month grids, day strings in the
 * viewer's time zone, and the exact schedules / counters query strings.
 * Days are 'YYYY-MM-DD' strings; arithmetic runs in UTC so the host's own
 * zone and DST never shift a day.
 */

export type Ymd = string

export interface Month {
  year: number
  /** 1–12. */
  month: number
}

export interface DayRange {
  from: Ymd
  to: Ymd
}

export type ScheduleType = 'post' | 'chat' | 'stream'

/** OnlyFans repeats filter[type][] in this order whatever was checked first. */
export const SCHEDULE_TYPES: readonly ScheduleType[] = ['post', 'chat', 'stream']

/** OnlyFans asks for 20 and answers the whole day / month anyway. */
export const PAGE = 20

const pad = (n: number) => String(n).padStart(2, '0')

function toUtc(day: Ymd): Date {
  const [y = 1970, m = 1, d = 1] = day.split('-').map(Number)
  return new Date(Date.UTC(y, m - 1, d))
}

function fromUtc(d: Date): Ymd {
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`
}

/** The calendar day of an instant in a time zone. */
export function dayInZone(at: string | Date, timeZone: string): Ymd {
  const date = typeof at === 'string' ? new Date(at) : at
  // en-CA formats as YYYY-MM-DD.
  return new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(date)
}

export function todayIn(timeZone: string, now: Date = new Date()): Ymd {
  return dayInZone(now, timeZone)
}

/** The browser's IANA zone (what OnlyFans sends as filter[timeZone]). */
export function browserTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
}

export function addDays(day: Ymd, n: number): Ymd {
  const d = toUtc(day)
  d.setUTCDate(d.getUTCDate() + n)
  return fromUtc(d)
}

export function monthOf(day: Ymd): Month {
  const [year = 1970, month = 1] = day.split('-').map(Number)
  return { year, month }
}

export function shiftMonth(m: Month, n: number): Month {
  const i = m.year * 12 + (m.month - 1) + n
  return { year: Math.floor(i / 12), month: (i % 12) + 1 }
}

export function firstOf(m: Month): Ymd {
  return `${m.year}-${pad(m.month)}-01`
}

export function lastOf(m: Month): Ymd {
  return addDays(firstOf(shiftMonth(m, 1)), -1)
}

export const sameMonth = (a: Month, b: Month) => a.year === b.year && a.month === b.month

/** Weeks (Sunday first) covering the month: 4–6 rows, neighbour days included. */
export function monthGrid(m: Month): Ymd[][] {
  const first = firstOf(m)
  const last = lastOf(m)
  let day = addDays(first, -toUtc(first).getUTCDay())
  const end = addDays(last, 6 - toUtc(last).getUTCDay())
  const weeks: Ymd[][] = []
  while (day <= end) {
    const week: Ymd[] = []
    for (let i = 0; i < 7; i++) {
      week.push(day)
      day = addDays(day, 1)
    }
    weeks.push(week)
  }
  return weeks
}

const later = (a: Ymd, b: Ymd) => (a > b ? a : b)

/** Counters cover the whole grid, but nothing before today. */
export function countersRange(m: Month, today: Ymd): DayRange {
  const grid = monthGrid(m)
  const lastWeek = grid[grid.length - 1]!
  return { from: later(grid[0]![0]!, today), to: lastWeek[6]! }
}

/** The list layout shows the month from today (current month) or its first day. */
export function listRange(m: Month, today: Ymd): DayRange {
  return { from: later(firstOf(m), today), to: lastOf(m) }
}

/** ‹ is disabled on the current month: the queue has no past. */
export function canGoBack(m: Month, today: Ymd): boolean {
  return firstOf(m) > firstOf(monthOf(today))
}

function typeFilter(types: readonly ScheduleType[]): ScheduleType[] {
  return SCHEDULE_TYPES.filter((t) => types.includes(t))
}

export function countersQuery(range: DayRange, types: readonly ScheduleType[], timeZone: string): Record<string, QueryValue> {
  return {
    'filter[publishDate]': range.from,
    'filter[publishDateEnd]': range.to,
    'filter[type][]': typeFilter(types),
    'filter[timeZone]': timeZone,
  }
}

export function dayQuery(day: Ymd, types: readonly ScheduleType[], timeZone: string): Record<string, QueryValue> {
  return {
    'filter[publishDate]': day,
    'filter[publishDateEnd]': day,
    limit: PAGE,
    'filter[type][]': typeFilter(types),
    'filter[timeZone]': timeZone,
  }
}

export function listQuery(range: DayRange, types: readonly ScheduleType[], timeZone: string, offset: number): Record<string, QueryValue> {
  return {
    limit: PAGE,
    offset: offset || null,
    'filter[publishDateEnd]': range.to,
    'filter[publishDate]': range.from,
    'filter[type][]': typeFilter(types),
    'filter[timeZone]': timeZone,
  }
}
